import * as React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import type {
  FindingsDecisionActionability,
  FindingsDecisionKind,
  FindingsDecisionPolicyStatus,
  FindingsGroupBy,
  FindingsSeverity,
  FindingsSort,
  FindingsStatusFilter,
  FindingsView,
} from '../findings-url';
import type { FindingDoc } from '../findings-types';
import { useFindingsData } from '../use-findings-data';

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  serious: 1,
  moderate: 2,
  minor: 3,
  error: 0,
  warning: 2,
  info: 3,
};

type RepositoryFindingsQueryArgs = {
  selectedProjectId?: string;
  projectLoading: boolean;
  activeFilter: FindingsStatusFilter;
  activeView: FindingsView;
  activeRule: string | null;
  activeCode: string | null;
  activeCategory: string | null;
  activeSeverity: FindingsSeverity | null;
  activeTokenFilter: string | null;
  activeComponentFilter: string | null;
  activeAreaFilter: string | null;
  activeDecisionActionability: FindingsDecisionActionability | null;
  activeDecisionPolicyStatus: FindingsDecisionPolicyStatus | null;
  activeDecisionKind: FindingsDecisionKind | null;
  activeDecisionKey: string | null;
  groupBy: FindingsGroupBy;
  pageIndex: number;
  pageSize: number;
  searchQuery: string;
  sortBy: FindingsSort;
};

export function useRepositoryFindingsQuery({
  selectedProjectId,
  projectLoading,
  activeFilter,
  activeView,
  activeRule,
  activeCode,
  activeCategory,
  activeSeverity,
  activeTokenFilter,
  activeComponentFilter,
  activeAreaFilter,
  activeDecisionActionability,
  activeDecisionPolicyStatus,
  activeDecisionKind,
  activeDecisionKey,
  groupBy,
  pageIndex,
  pageSize,
  searchQuery,
  sortBy,
}: RepositoryFindingsQueryArgs) {
  const sourceFilter = activeView === 'all' ? undefined : activeView;
  const registryForComponentFilter = useQuery(
    api.componentRegistry.listByProject,
    selectedProjectId && activeComponentFilter
      ? { projectId: selectedProjectId as any }
      : 'skip',
  );
  const componentFilterFilePath = React.useMemo<string | null>(() => {
    if (!activeComponentFilter || !registryForComponentFilter) return null;
    const match = registryForComponentFilter.find(
      (entry) =>
        entry.publicSlug === activeComponentFilter ||
        entry.componentKey === activeComponentFilter,
    );
    return match?.lastSeenFilePath ?? null;
  }, [activeComponentFilter, registryForComponentFilter]);
  const componentRegistryLoading =
    !!activeComponentFilter && registryForComponentFilter === undefined;
  const componentFilterArg = activeComponentFilter
    ? (componentFilterFilePath ?? '__no-component-match__')
    : undefined;

  const pageArgs =
    selectedProjectId && !componentRegistryLoading
      ? {
          projectId: selectedProjectId as any,
          pageIndex,
          pageSize,
          status: activeFilter === 'all' ? undefined : activeFilter,
          source: sourceFilter,
          tokenFilter: activeTokenFilter ?? undefined,
          area: activeAreaFilter ?? undefined,
          code: activeCode ?? undefined,
          ruleId: activeRule ?? undefined,
          category: activeCategory ?? undefined,
          severity: activeSeverity ?? undefined,
          searchQuery: searchQuery || undefined,
          filePath: componentFilterArg,
          actionability:
            groupBy === 'decision'
              ? (activeDecisionActionability ?? undefined)
              : undefined,
          policyStatus:
            groupBy === 'decision'
              ? (activeDecisionPolicyStatus ?? undefined)
              : undefined,
          decisionKind:
            groupBy === 'decision'
              ? (activeDecisionKind ?? undefined)
              : undefined,
          decisionKey:
            groupBy === 'decision'
              ? (activeDecisionKey ?? undefined)
              : undefined,
        }
      : ('skip' as const);

  const countsArgs = selectedProjectId
    ? { projectId: selectedProjectId as any, source: sourceFilter }
    : ('skip' as const);

  const groupAggregateArgs =
    selectedProjectId && !componentRegistryLoading && groupBy !== 'none'
      ? {
          projectId: selectedProjectId as any,
          groupBy,
          status: activeFilter === 'all' ? undefined : activeFilter,
          source: sourceFilter,
          tokenFilter: activeTokenFilter ?? undefined,
          area: activeAreaFilter ?? undefined,
          code: activeCode ?? undefined,
          ruleId: activeRule ?? undefined,
          category: activeCategory ?? undefined,
          severity: activeSeverity ?? undefined,
          searchQuery: searchQuery || undefined,
          filePath: componentFilterArg,
          actionability:
            groupBy === 'decision'
              ? (activeDecisionActionability ?? undefined)
              : undefined,
          policyStatus:
            groupBy === 'decision'
              ? (activeDecisionPolicyStatus ?? undefined)
              : undefined,
          decisionKind:
            groupBy === 'decision'
              ? (activeDecisionKind ?? undefined)
              : undefined,
          decisionKey:
            groupBy === 'decision'
              ? (activeDecisionKey ?? undefined)
              : undefined,
        }
      : ('skip' as const);

  const data = useFindingsData({
    pageArgs,
    countsArgs,
    groupArgs: groupAggregateArgs,
    projectLoading,
  });

  const sortedFindings = React.useMemo(() => {
    const items = [...data.items] as FindingDoc[];
    if (sortBy === 'file') {
      items.sort(
        (a, b) =>
          (a.filePath ?? '').localeCompare(b.filePath ?? '') ||
          (a.line ?? 0) - (b.line ?? 0),
      );
    } else if (sortBy === 'severity') {
      items.sort(
        (a, b) =>
          (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9),
      );
    } else if (sortBy === 'category') {
      items.sort((a, b) =>
        (a.category ?? 'zzz').localeCompare(b.category ?? 'zzz'),
      );
    }
    return items;
  }, [data.items, sortBy]);

  return {
    ...data,
    sourceFilter,
    componentRegistryLoading,
    componentFilterArg,
    sortedFindings,
  };
}
